import React, { useEffect } from 'react';
import PriceTag from '../ui/PriceTag';
import { formatDateTime } from '../../utils/formatDate';
import './OrderDetailsModal.css';

const OrderDetailsModal = ({ order, onClose, locale, t, currency }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const resolvedCurrency = currency || 'USD';
  const Price = ({ amount, size = 'sm' }) => (
    <PriceTag amount={amount} currency={resolvedCurrency} locale={locale} size={size} color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
  );

  return order ? (
    <div className="order-details-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={t('orders.orderDetails')}>
      <div className="order-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="order-details-header">
          <h2>{t('orders.orderDetails')}</h2>
          <button type="button" className="order-details-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="order-details-body">
          <div className="order-details-section">
            <h3>{t('orders.orderInfo')}</h3>
            <div className="order-details-row">
              <span className="order-details-label">{t('orders.orderNumber')}:</span>
              <span className="order-details-value">#{order.id}</span>
            </div>
            {order.order_type === 'car' ? (
              <>
                <div className="order-details-row">
                  <span className="order-details-label">{t('orders.carOrder')}:</span>
                  <span className="order-details-value">🚗</span>
                </div>
                <div className="order-details-row">
                  <span className="order-details-label">{t('menu.cart.carNumber')}:</span>
                  <span className="order-details-value">{order.car_number ?? 'N/A'}</span>
                </div>
                <div className="order-details-row">
                  <span className="order-details-label">{t('menu.cart.carColor')}:</span>
                  <span className="order-details-value">
                    {order.car_color
                      ? t(`menu.cart.carColors.${order.car_color}`, { defaultValue: order.car_color })
                      : '—'}
                  </span>
                </div>
              </>
            ) : (
              <div className="order-details-row">
                <span className="order-details-label">{t('orders.table')}:</span>
                <span className="order-details-value">{order.table_number ?? 'N/A'}</span>
              </div>
            )}
            <div className="order-details-row">
              <span className="order-details-label">{t('orders.time')}:</span>
              <span className="order-details-value">
                {formatDateTime(order.created_at, locale)}
              </span>
            </div>
            <div className="order-details-row">
              <span className="order-details-label">{t('orders.status.label')}:</span>
              <span className={`order-details-value order-details-status order-details-status--${order.status}`}>
                {t(`orders.status.${order.status}`)}
              </span>
            </div>
          </div>
          <div className="order-details-section">
            <h3>{t('orders.items')}</h3>
            {order.items && order.items.length > 0 ? (
              order.items.map((item, idx) => (
                <div key={item.id || idx} className="order-details-item">
                  <div className="order-details-item-header">
                    <span className="order-details-item-qty">{item.quantity}×</span>
                    <span className="order-details-item-name">{item.name}</span>
                    <span className="order-details-item-price">
                      <Price amount={(parseFloat(item.price) || 0) * (item.quantity || 1)} />
                    </span>
                  </div>
                  {item.selected_addons && item.selected_addons.length > 0 && (
                    <div className="order-details-item-addons">
                      {item.selected_addons.map((addon, addonIdx) => (
                        <div key={addonIdx} className="order-details-addon">
                          <span>+ {addon.name}</span>
                          {(addon.price && parseFloat(addon.price) > 0) && (
                            <span><Price amount={addon.price} /></span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="order-details-empty-items">{t('orders.empty.message')}</p>
            )}
          </div>
          {order.special_instructions && (
            <div className="order-details-section">
              <h3>{t('orders.specialInstructions')}</h3>
              <p className="order-details-special-text">{order.special_instructions}</p>
            </div>
          )}
          <div className="order-details-section order-details-total-section">
            {order.subtotal != null && order.tax_amount != null && (
              <>
                <div className="order-details-row">
                  <span className="order-details-label">{t('tax.subtotal')}:</span>
                  <span className="order-details-value"><Price amount={order.subtotal} /></span>
                </div>
                <div className="order-details-row">
                  <span className="order-details-label">
                    {t('tax.taxLine', { label: 'VAT', rate: order.tax_rate })}
                    {order.tax_inclusive ? t('tax.includedSuffix') : ''}:
                  </span>
                  <span className="order-details-value"><Price amount={order.tax_amount} /></span>
                </div>
              </>
            )}
            <div className="order-details-row order-details-total-row">
              <span className="order-details-label">{t('orders.total')}:</span>
              <span className="order-details-value order-details-total-value">
                <Price amount={order.total_amount} size="md" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default OrderDetailsModal;
